import * as React from 'react';
import { useRef } from 'react';
import { Overlay } from '../components/Overlay';
import { callFunction } from '../utils/FirebaseUtils';
import Layer from '../data/Layer';
import { loadFile, pause } from '../utils';
import { storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { ThumbsGrid } from '../components/ThumbsGrid';
import { OverlaySearch } from '../components/OverlaySearch';
import { reportIssue, trackEvent } from '../utils/Tracker';
import { TemplateUI } from 'data/FlyerData';
import { LoadingMore } from '../components/LoadingMore';

// initialPhotos
// searchPhotos + query
// trackPhotoDownload + id

interface USPhoto {
  id: string;
  author: string;
  authorProfileUrl: string;
  smallUrl: string;
  largeUrl: string;
}

const parsePhotos = (data: any): USPhoto[] => {
  if (!data || !Array.isArray(data)) {
    console.error('Cannot parse Unsplash photos');
    console.log(data);
    return [];
  }
  // console.log(data);
  return data.map((p: any) => {
    const id = p.id;
    let author = p.user.first_name;
    if (p.user.last_name) {
      author += ' ' + p.user.last_name;
    }
    const authorProfileUrl = p.user.links.html;
    const smallUrl = p.urls.small;
    const largeUrl = p.urls.regular;
    return {
      id,
      author,
      authorProfileUrl,
      smallUrl,
      largeUrl
    };
  });
};

interface ThumbProps {
  data: USPhoto;
  onClick: (photo: USPhoto) => void;
}

const PhotoThumb: React.FunctionComponent<ThumbProps> = props => {
  const anchorEl = useRef<HTMLAnchorElement>(null);
  const { data } = props;
  return (
    <div
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target !== anchorEl.current) {
          props.onClick(data);
        }
      }}
    >
      <div className="thumbs-grid-thumb">
        <img
          src={data.smallUrl}
          // crossOrigin="anonymous"
        />
      </div>
      <p>
        by{' '}
        <a
          ref={anchorEl}
          href={`${data.authorProfileUrl}?utm_source=BeatFlyer&utm_medium=referral`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {data.author}
        </a>
      </p>
    </div>
  );
};

interface Props {
  layerOrProperty?: Layer | TemplateUI;
}

interface State {
  photos: USPhoto[];
  query: string | null;
  isAdding: boolean;
  isSearching: boolean;
  isLoadingMorePhotos: boolean;
  loadPage: number;
}

export class UnsplashPhotoSelector extends React.Component<Props, State> {
  state: State = {
    photos: [],
    query: null,
    isAdding: false,
    isSearching: true,
    isLoadingMorePhotos: false,
    loadPage: 1
  };

  async componentDidMount() {
    try {
      const result = await callFunction('initialPhotos', {
        page: 1
      });
      this.setState({
        photos: parsePhotos(result.data),
        isSearching: false
      });
    } catch (e) {
      alert(e);
      reportIssue(e);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.photos.length !== this.state.photos.length) {
      this.gridDidScroll();
    }
  }

  gridDidScroll = (event?: React.UIEvent<HTMLDivElement>) => {
    // console.log('--- grid --- scroll');
    const { isLoadingMorePhotos, isSearching } = this.state;
    if (this.hasReachedTheBottom() && !isLoadingMorePhotos && !isSearching) {
      this.loadMorePhotos();
    }
  };

  hasReachedTheBottom = (): boolean => {
    const element = document.querySelector('.overlay-content');
    if (!element) {
      return true;
    }
    return element.scrollHeight - element.scrollTop === element.clientHeight;
  };

  onSearchSubmit = async (value: string) => {
    value = value.trim();
    if (value.length) {
      // console.log(`Search: "query"`);
      this.setState({
        query: value,
        isSearching: true,
        loadPage: 1
      });
      try {
        const result = await callFunction('searchPhotos', {
          query: value
        });
        this.setState({
          photos: parsePhotos(result.data.results),
          isSearching: false
        });
      } catch (e) {
        alert(e);
        reportIssue(e);
        this.setState({ isSearching: false });
      }
    }
  };

  didSelectPhoto = async (photo: USPhoto) => {
    trackEvent('Add Unsplash Photo', { id: photo.id });
    this.setState({ isAdding: true });
    try {
      await callFunction('trackPhotoDownload', { id: photo.id });
      const blob = await loadFile(photo.largeUrl);
      const file = new File([blob], 'unsplash.jpg');
      storeDispatch(Actions.closeModal());
      await pause(1);
      const { layerOrProperty } = this.props;
      if (!layerOrProperty || layerOrProperty instanceof Layer) {
        storeDispatch(Actions.addLocalGraphics([file], layerOrProperty, true));
      } else {
        storeDispatch(Actions.changeTemplateProperty(layerOrProperty, file));
      }
    } catch {
      this.setState({ isAdding: false });
    }
  };

  loadMorePhotos = async () => {
    const { query, loadPage, photos } = this.state;
    this.setState({ isLoadingMorePhotos: true });
    const page = loadPage + 1;
    console.log('LOAD MORE PHOTOS: page:' + page);
    let data: any;
    if (query) {
      data = (
        await callFunction('searchPhotos', {
          query,
          page
        })
      ).data.results;
    } else {
      data = (
        await callFunction('initialPhotos', {
          page
        })
      ).data;
    }
    const newPhotos = parsePhotos(data).filter(p => {
      for (const ep of photos) {
        if (ep.id === p.id) {
          return false;
        }
      }
      return true;
    });
    this.setState({
      photos: photos.concat(newPhotos),
      loadPage: page,
      isLoadingMorePhotos: false
    });
  };

  render() {
    const { isAdding, query, photos, isSearching, isLoadingMorePhotos } =
      this.state;
    if (isAdding) {
      return (
        <Overlay>
          <h1>Adding photo...</h1>
        </Overlay>
      );
    }

    const onUnsplash = (
      <>
        on{' '}
        <a
          href="https://unsplash.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Unsplash
        </a>
      </>
    );

    let title = query ? (
      photos.length ? (
        <>
          Showing results for "{query}" {onUnsplash}
        </>
      ) : (
        <>
          No result found for "{query}" {onUnsplash}
        </>
      )
    ) : (
      <>Popular photos {onUnsplash}</>
    );

    if (isSearching) {
      title = query ? (
        <>Searching "{query}"...</>
      ) : (
        <>Loading popular photos...</>
      );
    }

    return (
      <Overlay onScroll={this.gridDidScroll} alignTop={true} fullWidth={true}>
        <ThumbsGrid
          title={title}
          dim={isSearching}
          subtitleChild={
            <div>
              <OverlaySearch
                onSubmit={this.onSearchSubmit}
                focusAtStart={true}
              />
            </div>
          }
        >
          {photos.map(p => (
            <PhotoThumb data={p} key={p.id} onClick={this.didSelectPhoto} />
          ))}
        </ThumbsGrid>
        {isLoadingMorePhotos && <LoadingMore />}
      </Overlay>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modals/UnsplashPhotoSelector.tsx